import * as React from 'react'
import styled from 'styled-components'
import DatePicker from 'react-datepicker-styled-components'
import { colors } from '../../styleConstants'
import { Dialog } from '../common/Dialog'
import { ReservationState, Modal } from './useReservationState'
import { Mode, useEditAttributeState } from './useEditAttributeState'
import { I18nCatalogue } from '../../i18n/reservation/types'
import { Select } from '../common/Select'
import { Menu } from '../../types/menu'
import { HairStylist } from '../../types/hairStylist'
import { createReservedSpot } from '../../types/reservation'
import moment from 'moment'

interface Props {
  readonly catalogue: I18nCatalogue
  readonly pageState: ReservationState
  readonly stylist: HairStylist
  readonly special_spots: { value: string; label: string }[]
  readonly menus: Menu[]
  readonly onCreated?: () => void
}

interface State {
  readonly selectedSalonId: string
  readonly selectedSpotNumber: string
  readonly selectedSpecialSpotId: string
  readonly openTime: string
}

export function RegistReservation({ catalogue, pageState, stylist, special_spots, menus, onCreated }: Props) {
  const notificationId = `regist-reservation-` + pageState.selectedTime
  const inputReservationReleaseDateRef = React.useRef<HTMLInputElement>(null)
  const { actionState, onClose, onAction } = useEditAttributeState(pageState, notificationId, '')
  const action = onAction(callCreateReservation, catalogue.regist)
  const salons = stylist.salons
  const spotOption = [
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
  ]

  const salonOption =
    salons.length > 0
      ? salons.map(salon => {
          return { value: salon.id.toString(), label: salon.name }
        })
      : [{ value: '', label: '' }]

  const selectedSalonId = salons.length > 0 ? salons[0].id.toString() : ''
  const selectedSpotNumber = '1'
  const selectedSpecialSpotId = ''
  const openTime = moment(new Date(), 'YYYY/MM/DD').format('YYYY/MM/DD HH:mm')

  const [state, setState] = React.useState<State>({
    selectedSalonId,
    selectedSpotNumber,
    selectedSpecialSpotId,
    openTime,
  })

  async function callCreateReservation() {
    let releaseDate = ''

    if (state.selectedSpecialSpotId) {
      if (!menus.some(menu => menu.special_spot)) {
        alert(catalogue.regist.noHasMenu)
        throw new Error('Failed to Reservation Spot')
      }
    } else {
      // 通常メニュー登録時にメニューが存在していない場合はエラーとする
      // そのため、メニュー一覧から特定枠でないメニューが存在しているかをチェック
      if (!menus.some(menu => !menu.special_spot)) {
        alert(catalogue.regist.noHasMenu)
        throw new Error('Failed to Reservation Spot')
      }
    }

    releaseDate = state.openTime
      ? moment(state.openTime, 'YYYY/MM/DD')
          .startOf('day')
          .format('YYYY/MM/DD HH:mm:ss')
      : moment()
          .startOf('day')
          .subtract({ days: 1 })
          .format('YYYY/MM/DD HH:mm:ss')

    const [isSuccess, reponseStatus] = await createReservedSpot(
      stylist.id.toString(),
      state.selectedSalonId,
      pageState.selectedTime + ':00',
      state.selectedSpotNumber,
      releaseDate,
      state.selectedSpecialSpotId,
    )
    if (!isSuccess) {
      if (reponseStatus === 422) {
        alert(catalogue.regist.spotError)
      } else {
        alert(catalogue.regist.failed)
      }
      throw new Error('Failed to Reservation Spot')
    } else {
      if (onCreated) {
        onCreated()
      }
    }

    pageState.setState({
      modal: Modal.None,
      selectedTime: '',
      reservedSpot: null,
      cancelReservation: null,
      spots: [],
      isMobile: false,
    })
  }

  function setSelectedSalonId(event: React.ChangeEvent<HTMLSelectElement>) {
    setState({
      selectedSalonId: event.target.value,
      selectedSpotNumber: state.selectedSpotNumber,
      selectedSpecialSpotId: state.selectedSpecialSpotId,
      openTime: state.openTime,
    })
  }

  function setSelectedSpotNumber(event: React.ChangeEvent<HTMLSelectElement>) {
    setState({
      selectedSalonId: state.selectedSalonId,
      selectedSpotNumber: event.target.value,
      selectedSpecialSpotId: state.selectedSpecialSpotId,
      openTime: state.openTime,
    })
  }

  function setSelectedSpecialSpots(event: React.ChangeEvent<HTMLSelectElement>) {
    setState({
      selectedSalonId: state.selectedSalonId,
      selectedSpotNumber: state.selectedSpotNumber,
      selectedSpecialSpotId: event.target.value,
      openTime: state.openTime,
    })
  }

  const handleDateChange = (date: any) => {
    let targetDate = moment(date, 'YYYY/MM/DD').startOf('day')
    let today = moment(new Date(), 'YYYY/MM/DD').startOf('day')
    const spotDate = moment(pageState.selectedTime, 'YYYY/MM/DD').startOf('day')

    // 公開日は本日より後の日付か
    if (!targetDate.isSameOrAfter(today, 'day')) return alert('本日以降の日付を設定してください')
    // 予約日時は公開日より後の日付か
    if (!spotDate.isSameOrAfter(targetDate, 'day')) return alert('予約日時より前の日付を設定してください')

    setState({
      ...state,
      openTime: moment(date, 'YYYY/MM/DD').format('YYYY/MM/DD HH:mm'),
    })
  }

  return (
    <Dialog
      onClose={onClose}
      title={catalogue.regist.title}
      data-test="Regist-reservation-of-modal"
      actions={[
        { label: catalogue.cancel, look: 'white' },
        {
          label: catalogue.done,
          look: 'primary',
          onClick: action,
        },
      ]}>
      {actionState.mode === Mode.Failed && <ErrorMessage>{actionState.message}</ErrorMessage>}
      <DialogItem>
        <span style={{ float: 'left', marginRight: '18px' }}>{catalogue.reservationDateTime}:</span>
        <span>{pageState.selectedTime}</span>
      </DialogItem>
      <DialogItem>
        <span style={{ marginRight: '18px' }}>{catalogue.salon}:</span>
        <Select options={salonOption} onChange={event => setSelectedSalonId(event)} />
      </DialogItem>
      <DialogItem>
        <span style={{ marginRight: '18px' }}>{catalogue.reservablePeople}:</span>
        <Select options={spotOption} onChange={event => setSelectedSpotNumber(event)} />
      </DialogItem>
      <DialogItem>
        <span style={{ float: 'left', paddingTop: '5px' }}>{catalogue.reservationReleaseDate}:</span>
        <label className={'date-picker-label'}>
          <DatePicker
            wrapperClassName="datePicker"
            selected={new Date(state.openTime)}
            onChange={handleDateChange}
            ref={inputReservationReleaseDateRef}></DatePicker>
          <img src="/static/calender.svg" />
        </label>
      </DialogItem>
      {stylist.specialSpotFlag && (
        <DialogItem>
          <span style={{ marginRight: '18px' }}>{catalogue.specialSpot}:</span>
          <Select options={special_spots} onChange={event => setSelectedSpecialSpots(event)} />
        </DialogItem>
      )}
    </Dialog>
  )
}
const DialogItem = styled.div`
  margin: 18px;
  height: 36px;

  .date-picker-label {
    display: inline-flex;

    > img {
      width: 20px;
      position: relative;
      right: 50%;
    }
  }

  .react-datepicker__input-container > input {
    font-size: 18px;
    width: 60%;
  }
`

const ErrorMessage = styled.div`
  font-weight: bold;
  color: ${colors.error};
`
